import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// --- Import ONLY solid tech icons ---
import {
  faCode, faLaptopCode, faDatabase, faServer, faNetworkWired,
  faMicrochip, faTerminal, faCloud, faMobileAlt, faBug, faCogs,
  faKeyboard, faMouse, faHdd, faMemory, faCodeBranch, faProjectDiagram,
  faFileCode, faFilter, faSitemap, faShieldAlt, faKey, faUnlockAlt,
  faUserSecret, faBolt, faRocket, faAtom, faFlask, faBroadcastTower,
  faSatelliteDish, faWifi, faPlug, faPowerOff,
  faBatteryFull, faSimCard, faSdCard, faEthernet, faGlobe, faMapMarkedAlt,
  faRoute, faExchangeAlt, faSyncAlt, faRandom, faInfinity, faLink,
  faUnlink, faCut, faCopy, faPaste, faSave, faUpload, faDownload,
  faPrint, faSearch, faSlidersH, faTools, faWrench, faHammer, faScrewdriver
  // --- REMOVED Brand Icons Import ---
} from '@fortawesome/free-solid-svg-icons';

// Combine ONLY solid icons into one array
const techIcons = [
  faCode, faLaptopCode, faDatabase, faServer, faNetworkWired, faMicrochip,
  faTerminal, faCloud, faMobileAlt, faBug, faCogs, faKeyboard, faMouse,
  faHdd, faMemory, faCodeBranch, faProjectDiagram, faFileCode, faFilter,
  faSitemap, faShieldAlt, faKey, faUnlockAlt, faUserSecret, faBolt,
  faRocket, faAtom, faFlask, faBroadcastTower, faSatelliteDish, faWifi,
  faPlug, faPowerOff, faBatteryFull, faSimCard, faSdCard,
  faEthernet, faGlobe, faMapMarkedAlt, faRoute, faExchangeAlt, faSyncAlt,
  faRandom, faInfinity, faLink, faUnlink, faCut, faCopy, faPaste, faSave,
  faUpload, faDownload, faPrint, faSearch, faSlidersH, faTools, faWrench,
  faHammer, faScrewdriver,
  // --- REMOVED Brand Icons ---
];


// Simple component to render the "Tech" background SVG with icons
const TechBackground = ({ colors, renderWidth = 210 }) => {
  const iconColors = useMemo(() => [
    colors?.primary || '#cccccc',
    colors?.secondary || '#aaaaaa',
    colors?.tertiary || '#888888',
    colors?.quaternary || '#666666',
    colors?.accent1 || '#444444',
    colors?.accent2 || '#dddddd',
    colors?.accent3 || '#bbbbbb',
    colors?.accent4 || '#999999',
  ].filter(Boolean), [colors]);

  const height = 297;
  const margin = 5;
  const iconSize = 3.8;
  const iconOpacity = 0.25;
  // --- Define density instead of fixed totalIcons ---
  // Adjust this value to control overall density (icons per mm of perimeter)
  const density = 0.15; // e.g., 0.15 icons per mm

  const generatedIcons = useMemo(() => {
    if (iconColors.length === 0) return [];

    // --- Calculate totalIcons based on perimeter and density ---
    const perimeter = (2 * renderWidth) + (2 * height);
    const totalIcons = Math.round(perimeter * density); // Calculate needed icons

    const icons = [];
    // --- Use the calculated totalIcons in the loop ---
    for (let i = 0; i < totalIcons; i++) {
      let x, y;
      const edge = Math.floor(Math.random() * 4); // 0: top, 1: bottom, 2: left, 3: right

      // Placement logic remains the same (within margin)
      switch (edge) {
        case 0: // Top margin area
          x = Math.random() * renderWidth;
          y = Math.random() * margin;
          break;
        case 1: // Bottom margin area
          x = Math.random() * renderWidth;
          y = height - (Math.random() * margin);
          break;
        case 2: // Left margin area
          x = Math.random() * margin;
          y = Math.random() * height;
          break;
        case 3: // Right margin area
          x = renderWidth - (Math.random() * margin);
          y = Math.random() * height;
          break;
        default: continue;
      }

      // Adjust for top-left positioning of foreignObject
      const finalX = x - iconSize / 2;
      const finalY = y - iconSize / 2;

      // Select random icon, color, and rotation
      const randomIcon = techIcons[Math.floor(Math.random() * techIcons.length)];
      const randomColor = iconColors[i % iconColors.length];
      const randomRotation = Math.random() * 360;

      icons.push({
        id: `icon-${i}`,
        x: finalX,
        y: finalY,
        icon: randomIcon,
        color: randomColor,
        rotation: randomRotation,
      });
    }
    return icons;
  }, [iconColors, renderWidth]);

  if (!colors || iconColors.length === 0) {
    console.warn('TechBackground: No valid colors received or generated. Rendering nothing.', colors);
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        maxWidth: 'none !important',
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${renderWidth} ${height}`}
        preserveAspectRatio="xMidYMid slice"
        style={{ display: 'block', maxWidth: 'none' }}
      >
        {generatedIcons.map(iconData => (
          <foreignObject
            key={iconData.id}
            x={iconData.x}
            y={iconData.y}
            width={iconSize}
            height={iconSize}
            opacity={iconOpacity}
            style={{
              transform: `rotate(${iconData.rotation}deg)`,
              transformOrigin: 'center center',
              overflow: 'visible'
            }}
            viewBox="0 0 10 10"
            preserveAspectRatio="xMidYMid meet"
          >
            <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FontAwesomeIcon
                icon={iconData.icon}
                style={{
                  color: iconData.color,
                  fontSize: '80%',
                  verticalAlign: 'middle',
                  maxWidth: '100%',
                  maxHeight: '100%',
                }}
              />
            </div>
          </foreignObject>
        ))}
      </svg>
    </Box>
  );
};

export default TechBackground; 